/**
 * React Query hooks for inquiry management
 * Handles server state, caching, and invalidation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchInquiries,
  getInquiryDetail,
  updateInquiryStatus,
} from '@/modules/marketplace/services/inquiriesService';
import {
  Inquiry,
  InquiryFilters,
  InquiryRow,
  InquiryStatus,
  UseInquiriesReturn,
} from '@/modules/marketplace/types/inquiries.types';

/**
 * Query key factory for cache management
 */
export const inquiriesKeys = {
  all: ['inquiries'] as const,
  lists: () => [...inquiriesKeys.all, 'list'] as const,
  list: (filters: InquiryFilters) =>
    [...inquiriesKeys.lists(), { filters }] as const,
  details: () => [...inquiriesKeys.all, 'detail'] as const,
  detail: (id: string) => [...inquiriesKeys.details(), id] as const,
};

/**
 * Convert raw inquiry to table row format
 */
function formatInquiryRow(inquiry: Inquiry): InquiryRow {
  return {
    id: inquiry.id,
    date: new Date(inquiry.createdAt).toLocaleDateString('en-IN'),
    product: inquiry.productName,
    customerName: inquiry.customerName,
    customerEmail: inquiry.customerEmail,
    customerPhone: inquiry.customerPhone,
    message: inquiry.message,
    quantity: inquiry.quantity,
    quantityUnit: inquiry.quantityUnit,
    location: inquiry.location,
    status: inquiry.status,
    createdAt: inquiry.createdAt,
    contactedAt: inquiry.contactedAt,
    convertedAt: inquiry.convertedAt,
    closedAt: inquiry.closedAt,
  };
}

/**
 * Hook to fetch paginated inquiries with filters
 */
export function useInquiries(filters: InquiryFilters): UseInquiriesReturn {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: inquiriesKeys.list(filters),
    queryFn: () => fetchInquiries(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (cache time)
    retry: 3,
    retryDelay: (attemptIndex) =>
      Math.min(1000 * Math.pow(2, attemptIndex), 30000),
    refetchOnWindowFocus: false,
  });

  const inquiries = data?.content?.map(formatInquiryRow) ?? [];
  const pagination = {
    page: data?.number ?? 0,
    pageSize: data?.size ?? 20,
    totalItems: data?.totalElements ?? 0,
    totalPages: data?.totalPages ?? 0,
    hasNext: !(data?.last ?? true),
    hasPrev: !(data?.first ?? true),
  };

  return {
    inquiries,
    pagination,
    isLoading,
    isError,
    error: error instanceof Error ? error : null,
    refetch: async () => {
      const result = await refetch();
      return result.data;
    },
  };
}

/**
 * Hook to fetch single inquiry detail
 */
export function useInquiryDetail(id: string | null) {
  return useQuery({
    queryKey: id ? inquiriesKeys.detail(id) : ['inquiries', 'detail', null],
    queryFn: () => (id ? getInquiryDetail(id) : Promise.reject('No ID provided')),
    enabled: !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });
}

/**
 * Hook to update inquiry status
 */
export function useUpdateInquiryStatus() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: ({ id, status, notes, estimatedQuote }: {
      id: string;
      status: InquiryStatus;
      notes?: string;
      estimatedQuote?: number;
    }) => updateInquiryStatus(id, status, notes, estimatedQuote),
    onSuccess: (updatedInquiry) => {
      // Invalidate all inquiry lists and details
      queryClient.invalidateQueries({
        queryKey: inquiriesKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: inquiriesKeys.detail(updatedInquiry.id),
      });
    },
  });

  return {
    updateStatus: (id: string, status: InquiryStatus, notes?: string, estimatedQuote?: number) =>
      mutation.mutateAsync({ id, status, notes, estimatedQuote }),
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error instanceof Error ? mutation.error : null,
    reset: () => mutation.reset(),
  };
}

/**
 * Utility to prefetch inquiries
 */
export async function prefetchInquiries(
  queryClient: any,
  filters: InquiryFilters
) {
  await queryClient.prefetchQuery({
    queryKey: inquiriesKeys.list(filters),
    queryFn: () => fetchInquiries(filters),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Utility to invalidate inquiry cache
 */
export function invalidateInquiries(queryClient: any, id?: string) {
  if (id) {
    queryClient.invalidateQueries({
      queryKey: inquiriesKeys.detail(id),
    });
  } else {
    queryClient.invalidateQueries({
      queryKey: inquiriesKeys.lists(),
    });
  }
}
